import React from "react";
import { ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { InfoTip } from "./InfoTip";

export interface DashTileProps {
  title: string;
  value: string;
  sub: string;
  tooltip: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bgColor: string;
  /** When set, the whole tile becomes clickable (shows a chevron affordance). */
  onClick?: () => void;
}

/**
 * Design-refresh dashboard tile (used by both KPI rows).
 *
 * Flat + rounded, lighter type scale, soft icon. Optionally clickable (e.g. the
 * leads tile links to the filtered leads list) — then it shows a chevron and a
 * pointer/hover affordance. The InfoTip stops propagation so opening the
 * tooltip never triggers navigation.
 */
export const DashTile: React.FC<DashTileProps> = ({
  title,
  value,
  sub,
  tooltip,
  icon: Icon,
  color,
  bgColor,
  onClick,
}) => (
  <Card
    onClick={onClick}
    className={cn(
      "rounded-2xl border-border/60 shadow-none transition-colors",
      onClick ? "cursor-pointer hover:border-primary/40" : "hover:border-border",
    )}
  >
    <CardContent className="p-4 sm:p-5">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs sm:text-[13px] font-medium text-muted-foreground">{title}</p>
        <div className={`rounded-full p-2 sm:p-2.5 ${bgColor}`}>
          <Icon className={`h-4 w-4 sm:h-[18px] sm:w-[18px] ${color}`} />
        </div>
      </div>
      <div className="mt-3 sm:mt-5 flex items-end justify-between">
        <div>
          <p className="text-2xl sm:text-[28px] leading-none font-medium tracking-tight text-foreground">
            {value}
          </p>
          <p className="mt-1.5 sm:mt-2 text-xs text-muted-foreground">{sub}</p>
        </div>
        <div className="flex items-center gap-1">
          {onClick && <ChevronRight className="h-4 w-4 text-muted-foreground/40" />}
          <InfoTip text={tooltip} />
        </div>
      </div>
    </CardContent>
  </Card>
);
